import React, { useState, useEffect } from "react";
import CompanyHeader from "../../components/CompanyHeader";
import CompanySideMenu from "../../components/CompanySideMenu";
import { useNavigate, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import CustomToast from "../../components/CustomToast";
import CustomPagination from "../../components/CustomPagination";
import { fetchRankingList, setCurrentPageContentRanking } from "../../reducers/companyContent"
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useSelector, useDispatch } from "react-redux";
import noimage from "../../../assets/img/noimage.jpg";
import { maxLengthText } from "../../utils/format";
import { PERMISSIONS } from '../../constants/index';
import ReturnButton from "../../components/ReturnButton";
import CustomButton from "../../components/CustomButton";
import Copyright from "../../components/Copyright";

const ContentRankingList = () => {
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
    const currentUser = useSelector((state: any) => state.currentUser);
    const { staffPermission } = currentUser;
    const {
        rankingList,
        loading,
        success,
        error,
        countPage,
        currentPageContentRanking,
        countItems,
        pageSize,
        rankAOnly,
        selectedTypeOfTalent,
    } = useSelector((state: any) => state.companyContent);

    const navigate = useNavigate();

    const fetchRankingData = (pageNumber: any, pageSize: any) => {
        const data = {
            page: pageNumber,
            pageSize: pageSize,
            rankAOnly: rankAOnly ? 1 : 0,
            talentType: selectedTypeOfTalent?.length > 0 ? selectedTypeOfTalent.join(',') : null,
        }
        dispatch(fetchRankingList(data));
    }

    const handleChangePage = (pageNumber: any, pageSizeInput: any) => {
        fetchRankingData(pageNumber, pageSizeInput);
        dispatch(setCurrentPageContentRanking(pageNumber));
    };

    useEffect(() => {
        fetchRankingData(currentPageContentRanking, pageSize);
    }, [dispatch]);

    const location = useLocation();
    const successMsg = location.state?.successMsg;
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    useEffect(() => {
    if(successMsg) {
        enqueueSnackbar("I love snacks.", {
        content: (key) => (
            <CustomToast
            title={"メッセージ"}
            message={successMsg}
            variant={"success"}
            snackbarKey={key}
            closeSnackbar={closeSnackbar}
            />
        ),
        });
        navigate(location.pathname, { replace: true, state: {} });
    }
    }, [successMsg]);

    interface RankingItem {
        id: number;
        img: string;
        title: string;
        views: string;
        description: string;
        action: {
            name: string;
        };
    }
    const rankingListData: RankingItem[] = rankingList.map((item: any) => ({
        ...item,
        img: item.images ? item.images : noimage,
        description: item.contents,
        views: new Intl.NumberFormat().format(item.pv) + "PV",
        action: { name: "編集" },
    }));

    return (
        <>
        <CompanyHeader />
        <div className="mt-6 px-6 flex gap-6 relative">
            <CompanySideMenu />
            <div className="flex-1">
            <div className="mb-4 flex items-center gap-4">
                <ReturnButton onClick={() => navigate(-1)} />
                <span className="text-sm alu-text-base-1">/</span>
                <h3 className="text-xl leading-[1.875rem] alu-text-base-2 font-bold">
                コンテンツランキング
                </h3>
            </div>

            <div className="bg-white mb-4 rounded-lg p-6">
                <table className="table-auto w-full">
                <tbody>
                    {rankingListData.map((item: any, idx: any) => (
                    <tr className="group border-b border-b-[#E0E0E0]" key={idx}>
                        <td className="min-w-2 py-2 text-right font-bold text-sm leading-[1.3125] alu-text-base-2 align-top">
                            {item.ranking}
                        </td>
                        <td className="min-w-[100px] px-4 py-2 text-left font-normal text-sm leading-[1.3125] alu-text-base-2">
                        <img
                            onClick={() => navigate("/company/news/detail/" + item.id)}
                            className="w-16 h-16 object-cover group-hover:opacity-80 group-hover:cursor-pointer"
                            src={item.img}
                            alt={`thumbnail-${idx}`}
                        />
                        </td>
                        <td className="min-w-[220px] px-4 py-2 text-left font-bold text-base alu-text-base-2 align-top">
                        <div>
                            <h5 
                                className="group-hover:underline underline-offset-1 hover:cursor-pointer mb-1 text-sm leading-[1.3125rem] font-bold whitespace-pre-wrap alu-text-base-2 text-link-with-hover"
                                onClick={() => navigate("/company/news/detail/" + item.id)}
                            >
                            {maxLengthText(item.title, 40)}
                            </h5>
                            <p className="text-sm leading-[1.3125rem] font-normal alu-text-base-1">
                            {item.views}
                            </p>
                        </div>
                        </td>
                        <td className="min-w-[425px] px-4 py-2 text-left font-normal text-sm leading-[1.3125] alu-text-base-2 align-top">
                            <p className="whitespace-pre-wrap line-clamp-2">
                                {maxLengthText(item.description, 100)}
                            </p>
                        </td>
                        {staffPermission[PERMISSIONS.NEWS_EDIT] && (
                        <td
                            className="min-w-[100px] px-4 py-2 text-left font-normal text-sm leading-[1.3125] align-top"
                        >
                            <CustomButton
                                onClick={() => navigate("/company/news/edit/" + item.id, { state: { isFromContentRanking: true } })}
                                variant="text"
                                size="sm"
                            >
                                {item.action.name}
                            </CustomButton>
                        </td>
                        )}
                    </tr>
                    ))}
                </tbody>
                </table>
            </div>
            {countPage > 1 && (
            <div className="flex items-center justify-center">
                <CustomPagination 
                    total={countItems} 
                    currentPage={currentPageContentRanking}
                    pageSize={pageSize} 
                    onPageChange={handleChangePage} 
                />
            </div>
            )}
            </div>
        </div>
        <Copyright view="company" />
        </>
    );
};

export default ContentRankingList;